import { t } from '../i18n';

/**
 * Return the display name for a player (Player Identity V1).
 *
 * The backend's `display_name` IS the canonical name (username → email
 * local-part → 'Player', with bots keeping their bot names — see backend
 * User#public_name). The client renders it verbatim and never reconstructs
 * a name from raw email (game payloads stopped carrying emails at P0
 * privacy; the old email fallback here was dead code that violated the
 * one-rule principle).
 *
 * Localization (Phase 9): bots with a STRUCTURED `bot_difficulty` value get
 * a localized label resolved by that stable value — never by parsing the
 * English display_name. Bots without the field (older payloads, tutorial
 * fixtures) and all humans keep display_name verbatim. This is the single
 * label seam shared by React (GameScreen rows, overlays) and Phaser
 * (DesktopPlayerCards), so both sides always agree.
 */
/** Minimal structural shape shared by every per-player payload variant
 *  (Player, GamePlayersUpdateDetail, RevealedPlayer, SessionPlayer). */
export interface LabeledPlayer {
  display_name?: string | null;
  is_bot?: boolean;
  bot_difficulty?: string | null;
  /** Scripted-presentation marker set ONLY at the tutorial transport's
   *  stamping boundary — never present on production payloads. 'self'
   *  localizes the local tutorial seat (You/Tú) without parsing the
   *  fixture's English display_name. */
  presentation_role?: 'self' | null;
}

/** Localized label for a bot with a known structured difficulty; null when
 *  the difficulty is missing/unknown (caller falls back to display_name). */
export function localizedBotLabel(player: LabeledPlayer | null | undefined): string | null {
  if (!player?.is_bot) return null;
  switch (player.bot_difficulty) {
    case 'easy':   return String(t('gameplay:bot.easy'));
    case 'medium': return String(t('gameplay:bot.medium'));
    case 'hard':   return String(t('gameplay:bot.hard'));
    default:       return null; // unknown/missing → English display_name fallback
  }
}

export function getPlayerName(player: LabeledPlayer | null | undefined, fallback = 'Player'): string {
  if (!player) return fallback;
  if (player.presentation_role === 'self' && !player.is_bot) {
    return String(t('gameplay:fallback.you'));
  }
  return localizedBotLabel(player) ?? (player.display_name || fallback);
}
